import { useEffect, useRef } from 'react';
import type { FormikProps, FormikErrors } from 'formik';

export type TabPrefixes = Record<number, string[]>;

/**
 * Flattens a Formik error object into dot-paths (e.g. "customer.name", "items.0.qty")
 * Only string leaves are treated as "error paths".
 */
const flattenErrorPaths = (errObj: any, parent = ''): string[] => {
  if (!errObj) return [];
  if (typeof errObj === 'string') return parent ? [parent] : [];

  if (Array.isArray(errObj)) {
    return errObj.flatMap((v, i) =>
      flattenErrorPaths(v, parent ? `${parent}.${i}` : `${i}`),
    );
  }

  if (typeof errObj === 'object') {
    return Object.entries(errObj).flatMap(([k, v]) =>
      flattenErrorPaths(v, parent ? `${parent}.${k}` : k),
    );
  }

  return [];
};

/**
 * Flattens a Formik touched object into dot-paths for fields that are touched.
 * Example leaves: true => path is included.
 */
const flattenTouchedPaths = (touchedObj: any, parent = ''): string[] => {
  if (!touchedObj) return [];

  // Formik touched leaves are typically boolean true
  if (touchedObj === true) return parent ? [parent] : [];

  if (Array.isArray(touchedObj)) {
    return touchedObj.flatMap((v, i) =>
      flattenTouchedPaths(v, parent ? `${parent}.${i}` : `${i}`),
    );
  }

  if (typeof touchedObj === 'object') {
    return Object.entries(touchedObj).flatMap(([k, v]) =>
      flattenTouchedPaths(v, parent ? `${parent}.${k}` : k),
    );
  }

  return [];
};

const touchAll = (values: any): any => {
  if (Array.isArray(values)) return values.map(touchAll);
  if (values && typeof values === 'object') {
    const out: any = {};
    for (const k of Object.keys(values)) out[k] = touchAll(values[k]);
    return out;
  }
  return true;
};

const getIn = (obj: any, path: string) =>
  path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

const hasErrorAtPath = (errors: any, path: string) => {
  const v = getIn(errors, path);
  if (!v) return false;

  // yup/string leaf errors
  if (typeof v === 'string') return true;

  // sometimes arrays/objects can exist; treat non-empty as error-ish
  if (Array.isArray(v)) return v.some(Boolean);

  if (typeof v === 'object') return Object.keys(v).length > 0;

  return false;
};

/**
 * OLD behavior (unchanged):
 * Returns tabs that have ANY errors, regardless of touched/submitted state.
 * This is useful for submit-time tab switching (middleware).
 */
export const getTabsWithErrors = (
  tabPrefixes: TabPrefixes,
  errors: FormikErrors<any>,
) => {
  const paths = flattenErrorPaths(errors);

  return Object.keys(tabPrefixes)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((tabIndex) => {
      const prefixes = tabPrefixes[tabIndex] ?? [];
      return paths.some((p) =>
        prefixes.some((pref) => p === pref || p.startsWith(`${pref}.`)),
      );
    });
};

/**
 * NEW behavior:
 * Returns tabs that have "VISIBLE" errors:
 * - the field is touched (or was touched by your submit middleware)
 * - AND that field has an error
 *
 * This prevents tabs going red on initial modal open if Formik has errors but nothing is touched yet.
 */
export const getTabsWithVisibleErrors = (
  tabPrefixes: TabPrefixes,
  errors: FormikErrors<any>,
  touched: any,
) => {
  const touchedPaths = flattenTouchedPaths(touched);

  return Object.keys(tabPrefixes)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((tabIndex) => {
      const prefixes = tabPrefixes[tabIndex] ?? [];

      return touchedPaths.some((p) => {
        const inThisTab = prefixes.some(
          (pref) => p === pref || p.startsWith(`${pref}.`),
        );
        return inThisTab && hasErrorAtPath(errors, p);
      });
    });
};

/**
 * Middleware:
 * Detects submit attempts, waits for validation, touches all fields if errors,
 * and calls back with tabs-with-errors so you can switch tabs.
 */
export const useFormikTabErrorMiddleware = <Values,>(
  formik: FormikProps<Values>,
  tabPrefixes: TabPrefixes,
  onTabsWithError: (tabsWithError: number[]) => void,
) => {
  const pendingSubmitRef = useRef<number | null>(null);

  // detect submit attempts
  useEffect(() => {
    if (formik.submitCount > 0) pendingSubmitRef.current = formik.submitCount;
  }, [formik.submitCount]);

  // once validation finishes, if there are errors -> trigger tab logic
  useEffect(() => {
    const pending = pendingSubmitRef.current === formik.submitCount;
    if (!pending) return;

    // wait until validation completes
    if (formik.isValidating) return;

    if (Object.keys(formik.errors ?? {}).length > 0) {
      // this is what makes your fields show errors (touched becomes true everywhere)
      formik.setTouched(touchAll(formik.values), true);

      // for tab switching, "any error anywhere" is correct
      onTabsWithError(getTabsWithErrors(tabPrefixes, formik.errors as any));
    }

    pendingSubmitRef.current = null;
  }, [
    formik.errors,
    formik.isValidating,
    formik.submitCount,
    formik.values,
    tabPrefixes,
    onTabsWithError,
    formik,
  ]);
};

/**
 * Generates a style object for a tab based on whether it has an error.
 *
 * @param hasError - A boolean indicating whether the tab has an error.
 * @returns An object containing the styles for the tab. If `hasError` is true,
 *          the tab's color will be set to "error.main". Additionally, if the tab
 *          is selected (`.Mui-selected`), the color will also be set to "error.main".
 */
export const tabErrorSx = (hasError: boolean) => ({
  color: hasError ? 'error.main' : undefined,
  '&.Mui-selected': {
    color: hasError ? 'error.main' : undefined,
  },
});

/**
 * Generates a style object for a tab indicator based on the presence of an error.
 *
 * @param hasError - A boolean indicating whether there is an error.
 * @returns An object containing the `backgroundColor` property set to "error.main" if there is an error, or `undefined` otherwise.
 */
export const tabIndicatorErrorSx = (hasError: boolean) => ({
  backgroundColor: hasError ? 'error.main' : undefined,
});

/**
 * Determines if a specific tab has an error based on its index.
 *
 * @param i - The index of the tab to check.
 * @param tabsWithError - An array of tab indices that have errors.
 * @returns `true` if the tab at the given index has an error, otherwise `false`.
 */
export const tabHasError = (i: number, tabsWithError: number[]) =>
  tabsWithError.includes(i);
